import { useMemo, useState } from "react";
import { Button } from "../ui/button";
import { Dialog, DialogContent, DialogTitle } from "../ui/dialog";
import { Textarea } from "../ui/textarea";
import type {
  ChapterConflictHunk,
  ChapterConflictResolution,
  ChapterConflictResolutionChoice,
  ThreeWayMergeResult,
} from "../../../shared/chapterSync/types";

function kindLabel(kind: ChapterConflictHunk["kind"]): string {
  switch (kind) {
    case "both_modified":
      return "Both edited";
    case "edit_vs_delete":
      return "Deleted here, edited elsewhere";
    case "delete_vs_edit":
      return "Edited here, deleted elsewhere";
    case "both_inserted_different":
      return "Different inserts";
    default:
      return kind;
  }
}

type Props = {
  open: boolean;
  merge: ThreeWayMergeResult | null;
  autoMergedBlockCount?: number;
  saving?: boolean;
  onClose: () => void;
  onApply: (resolutions: ChapterConflictResolution[]) => void;
  onKeepAllMine: () => void;
  onKeepAllTheirs: () => void;
};

export function ConflictReview({
  open,
  merge,
  autoMergedBlockCount,
  saving,
  onClose,
  onApply,
  onKeepAllMine,
  onKeepAllTheirs,
}: Props) {
  const conflicts = merge?.conflicts ?? [];
  const [choices, setChoices] = useState<Record<string, ChapterConflictResolutionChoice>>({});
  const [combined, setCombined] = useState<Record<string, string>>({});

  const allChosen =
    conflicts.length > 0 && conflicts.every((h) => Boolean(choices[h.id]));

  const summary = useMemo(() => {
    const n = typeof autoMergedBlockCount === "number" ? autoMergedBlockCount : merge?.autoMergedBlockCount;
    return typeof n === "number" && n > 0 ? ` (${n} paragraphs merged automatically)` : "";
  }, [autoMergedBlockCount, merge?.autoMergedBlockCount]);

  function setChoice(hunkId: string, choice: ChapterConflictResolutionChoice) {
    setChoices((prev) => ({ ...prev, [hunkId]: choice }));
  }

  function handleApply() {
    const resolutions: ChapterConflictResolution[] = conflicts.map((h) => {
      const choice = choices[h.id] ?? "mine";
      if (choice === "combined") {
        return { hunkId: h.id, choice, combinedText: combined[h.id] ?? h.mineText };
      }
      return { hunkId: h.id, choice };
    });
    onApply(resolutions);
  }

  return (
    <Dialog
      open={open && merge != null}
      onOpenChange={(next) => {
        if (!next && !saving) onClose();
      }}
    >
      <DialogContent className="flex max-h-[90vh] max-w-3xl flex-col gap-0 overflow-hidden p-0">
        <div className="border-b border-border px-4 py-3">
          <DialogTitle className="text-base font-semibold">Review conflicting edits</DialogTitle>
          <p className="mt-1 text-[0.8125rem] leading-snug text-muted-foreground">
            Edits from this device and another copy both changed this chapter. Choose which text to
            keep for each conflict{summary}.
          </p>
        </div>

        <div className="min-h-0 flex-1 space-y-3 overflow-y-auto px-4 py-3">
          {conflicts.map((hunk) => {
            const choice = choices[hunk.id];
            return (
              <section key={hunk.id} className="rounded-md border border-border bg-muted/30 p-3">
                <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                  <span className="text-muted-foreground text-[11px] font-medium uppercase tracking-wide">
                    {kindLabel(hunk.kind)}
                  </span>
                  <div className="flex flex-wrap gap-1.5">
                    <Button
                      type="button"
                      size="sm"
                      variant={choice === "mine" ? "default" : "secondary"}
                      disabled={saving}
                      onClick={() => setChoice(hunk.id, "mine")}
                    >
                      Keep mine
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant={choice === "theirs" ? "default" : "secondary"}
                      disabled={saving}
                      onClick={() => setChoice(hunk.id, "theirs")}
                    >
                      Keep theirs
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant={choice === "combined" ? "default" : "secondary"}
                      disabled={saving}
                      onClick={() => {
                        setChoice(hunk.id, "combined");
                        setCombined((prev) => ({
                          ...prev,
                          [hunk.id]: prev[hunk.id] ?? hunk.mineText,
                        }));
                      }}
                    >
                      Edit combined
                    </Button>
                  </div>
                </div>
                <div className="grid gap-2 sm:grid-cols-2">
                  <div>
                    <p className="text-muted-foreground mb-1 text-[11px] font-medium">This device</p>
                    <p className="whitespace-pre-wrap rounded border border-border bg-background p-2 text-[0.8125rem] leading-relaxed">
                      {hunk.mineText || <span className="italic opacity-60">Deleted</span>}
                    </p>
                  </div>
                  <div>
                    <p className="text-muted-foreground mb-1 text-[11px] font-medium">Other copy</p>
                    <p className="whitespace-pre-wrap rounded border border-border bg-background p-2 text-[0.8125rem] leading-relaxed">
                      {hunk.theirsText || <span className="italic opacity-60">Deleted</span>}
                    </p>
                  </div>
                </div>
                {choice === "combined" ? (
                  <div className="mt-2">
                    <p className="text-muted-foreground mb-1 text-[11px] font-medium">Combined text</p>
                    <Textarea
                      value={combined[hunk.id] ?? hunk.mineText}
                      onChange={(e) =>
                        setCombined((prev) => ({ ...prev, [hunk.id]: e.target.value }))
                      }
                      disabled={saving}
                      rows={4}
                    />
                  </div>
                ) : null}
              </section>
            );
          })}
        </div>

        <div className="flex flex-wrap items-center justify-between gap-2 border-t border-border px-4 py-3">
          <div className="flex flex-wrap gap-2">
            <Button type="button" size="sm" variant="secondary" disabled={saving} onClick={onKeepAllMine}>
              Keep all mine
            </Button>
            <Button type="button" size="sm" variant="secondary" disabled={saving} onClick={onKeepAllTheirs}>
              Keep all theirs
            </Button>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button type="button" size="sm" variant="ghost" disabled={saving} onClick={onClose}>
              Cancel
            </Button>
            <Button type="button" size="sm" disabled={saving || !allChosen} onClick={handleApply}>
              {saving ? "Applying…" : "Apply & sync"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function chapterSyncStatusLabel(status: string): string {
  switch (status) {
    case "dirty":
      return "Saving locally…";
    case "localSaved":
      return "Saved on this device";
    case "pushing":
      return "Syncing…";
    case "synced":
      return "Synced";
    case "offline":
      return "Offline";
    case "conflict":
      return "Conflict — review edits";
    case "error":
      return "Sync error";
    default:
      return "";
  }
}
